import picsioConfig from '../../../../../config';
import localization from '../../shared/strings';
import { defaultPresets, customPreset, defaultPresetConfig } from '../DialogPresets/config';
import buildDownloadModePresets from '../../helpers/assets/buildDownloadModePresets';
import buildDownloadSubmitConfig from '../../helpers/assets/buildDownloadSubmitConfig';
import { isBulkCopyCropAspect } from '../../helpers/assets/copyCropAspectPresets';

const { isMainApp } = picsioConfig;

function clonePreset(preset) {
  return { ...preset, data: { ...preset.data } };
}

/**
 * Split server presets so curated defaults (`isDefault: true` — Instagram Post,
 * Facebook Post, etc.) render before user-created ones. Relative order inside
 * each group is preserved (server returns them in insertion order).
 */
function splitByDefault(downloadPresets = []) {
  const defaults = [];
  const custom = [];
  downloadPresets.forEach((p) => {
    if (p && p.isDefault) defaults.push(p);
    else custom.push(p);
  });
  return { defaults, custom };
}

function buildCopyBasePresets(downloadPresets, shouldFormatBeDisabled) {
  if (!isMainApp || shouldFormatBeDisabled) {
    return [...defaultPresets].map(clonePreset);
  }
  const { defaults, custom } = splitByDefault(downloadPresets);
  // Order: Original, Custom, server defaults (isDefault:true), user-created.
  return [...defaultPresets, customPreset, ...defaults, ...custom].map(clonePreset);
}

export const copyStrategy = {
  variant: 'copy',

  buildPresets({ downloadPresets, shouldFormatBeDisabled }) {
    return {
      presets: buildCopyBasePresets(downloadPresets, shouldFormatBeDisabled),
      hasPdfProxyPresets: false,
      hasCustomVideoPresets: false,
    };
  },

  canShowPresets() {
    return Boolean(isMainApp);
  },

  canSavePreset() {
    return Boolean(isMainApp);
  },

  canShowConversionSettings({ shouldFormatBeDisabled }) {
    return !shouldFormatBeDisabled;
  },

  canShowExtraPanel() {
    return false;
  },

  getTitle({ selectionCount }) {
    return selectionCount > 1
      ? localization.COPY_ASSETS_DIALOG.bulkCopyTitle
      : localization.COPY_ASSETS_DIALOG.title;
  },

  getPrimaryLabel() {
    return localization.COPY_ASSETS_DIALOG.btnOk;
  },

  getCancelLabel() {
    return localization.COPY_ASSETS_DIALOG.btnCancel;
  },

  getPresetSelectorLabel() {
    return localization.COPY_ASSETS_DIALOG.presetSelectorLabel;
  },

  getPresetInfoText() {
    return localization.COPY_ASSETS_DIALOG.originalPresetInfoText();
  },

  getUnsupportedFormatsWarning({ unsupportedCount }) {
    if (!unsupportedCount) return null;
    return localization.COPY_ASSETS_DIALOG.unsupportedFormatsWarning(unsupportedCount);
  },

  getLockedSettingTooltip() {
    return localization.COPY_ASSETS_DIALOG.lockedSettingTooltip();
  },

  getSelectFormatFirstTooltip() {
    return localization.COPY_ASSETS_DIALOG.selectFormatFirstTooltip;
  },

  getSuggestImprovementLocation() {
    return localization.COPY_ASSETS_DIALOG.suggestImprovementLocation;
  },

  shouldDestroyOnSubmit: true,

  presetDialogStrings: {
    saveError: () => localization.COPY_ASSETS_DIALOG.saveCopyPresetError,
    removeError: () => localization.COPY_ASSETS_DIALOG.removeCopyPresetError,
    createPresetTitle: () => localization.COPY_ASSETS_DIALOG.createCopyPreset,
    savePresetTitle: () => localization.COPY_ASSETS_DIALOG.saveCopyPreset,
    removePresetTitle: () => localization.COPY_ASSETS_DIALOG.removeCopyPreset,
    removePresetText: (presetName) => localization.COPY_ASSETS_DIALOG.removeCopyPresetText(presetName),
  },

  buildSubmitPayload({
    selectedPresetId,
    selectedSettings,
    normalizedCrop,
    cropAspectPreset,
    presets,
    transformationMode,
  }) {
    // Always include a valid ratio so the zipper can centered-crop on either
    // bulk or single-asset copy (writeCroppedPng picks `crop` first, then
    // falls back to `cropAspect`).
    const cropAspect = isBulkCopyCropAspect(cropAspectPreset)
      ? cropAspectPreset
      : null;

    // UI-only flag: surfaces in the `CopyOptionsModalConfirmClicked` analytics
    // event (`presets.freeform`) when the user picked the Free Form chip in
    // the crop ratio panel. Stripped by `buildCopyConvertingParams` before the
    // request reaches the server, so it never hits the API payload.
    const isFreeform = cropAspectPreset === 'free';

    // Whitelist known conversion fields — preset.data may carry UI-only keys
    // (ratios, gravity-only crop object) that the server's Joi schema would
    // reject or that would shadow the normalized crop below.
    const {
      mimeType,
      quality,
      resizing,
      width,
      height,
      density,
      units,
      isArchive,
      organizeByCollections,
      withoutWatermark,
      resolution,
      usePdfProxy,
    } = selectedSettings || {};

    // Social media presets (Instagram Post, X Cover, …) are marked
    // `isDefault: true` by the server. For those we propagate the preset name
    // so the server can embed it in the copied asset filename (e.g.
    // `photo (Instagram Post).jpg`) — helps users search by preset later.
    const selectedPreset = Array.isArray(presets)
      ? presets.find((p) => p && p._id === selectedPresetId)
      : null;
    const socialPresetName = selectedPreset?.isDefault && selectedPreset?.name
      ? selectedPreset.name
      : null;

    return {
      presetId: selectedPresetId,
      mimeType,
      quality,
      resizing,
      width,
      height,
      density,
      units,
      isArchive,
      organizeByCollections,
      withoutWatermark,
      resolution,
      usePdfProxy,
      freeform: isFreeform,
      ...(transformationMode ? { transformationMode } : {}),
      ...(normalizedCrop ? { crop: normalizedCrop } : {}),
      ...(cropAspect ? { cropAspect } : {}),
      ...(socialPresetName ? { presetName: socialPresetName } : {}),
    };
  },
};

export const downloadStrategy = {
  variant: 'download',

  buildPresets({
    downloadPresets, shouldFormatBeDisabled, assets, selectionCount, forceOriginalOnly,
  }) {
    // When the website owner restricted download to the original format, skip
    // all preset permutations (PDF proxy, custom video, user presets) — the user
    // cannot pick a different format anyway.
    if (forceOriginalOnly) {
      return {
        presets: [
          {
            _id: 'original',
            name: 'Original',
            data: { ...defaultPresetConfig, mimeType: 'original' },
          },
        ],
        hasPdfProxyPresets: false,
        hasCustomVideoPresets: false,
      };
    }
    return buildDownloadModePresets({
      downloadPresets,
      shouldFormatBeDisabled,
      assets: assets || [],
      countAssets: selectionCount,
    });
  },

  canShowPresets({ forceOriginalOnly }) {
    // Proofing / public website users see a "Save as" selector with the
    // built-in Original + Custom slots (plus any PDF proxy / user presets)
    // unless the website owner has forced original-only downloads.
    return !forceOriginalOnly;
  },

  // Download dialog: allow saving new user presets only in the main app.
  // Proofing / public website viewers can pick Original / Custom but can't
  // persist a preset, so the "save as new preset" icon stays hidden there.
  canSavePreset() {
    return Boolean(isMainApp);
  },

  canShowConversionSettings({
    shouldFormatBeDisabled,
    hasPdfProxyPresets,
    hasCustomVideoPresets,
    forceOriginalOnly,
  }) {
    if (forceOriginalOnly) return false;
    if (shouldFormatBeDisabled) return false;
    if (!isMainApp) return true;
    // Settings stay visible for the "Original" preset as well — they just get disabled
    // inside <Settings> because selectedSettings.mimeType === 'original'. This matches
    // the AssetsCopyDialog behaviour.
    return !hasPdfProxyPresets && !hasCustomVideoPresets;
  },

  canShowExtraPanel() {
    return true;
  },

  getTitle({ selectionCount }) {
    return selectionCount > 1
      ? localization.DOWNLOADDIALOG.bulkDownloadTitle
      : localization.DOWNLOADDIALOG.titleDownloadOptions;
  },

  getPrimaryLabel({ selectionCount }) {
    return selectionCount > 1
      ? localization.DOWNLOADDIALOG.downloadWithAssetCount(selectionCount)
      : localization.DOWNLOADDIALOG.textDownload;
  },

  getCancelLabel() {
    return localization.DIALOGS.btnCancel;
  },

  getPresetSelectorLabel() {
    return localization.DOWNLOADDIALOG.presetSelectorLabel;
  },

  getPresetInfoText() {
    return localization.DOWNLOADDIALOG.originalPresetInfoText();
  },

  getUnsupportedFormatsWarning({ unsupportedCount }) {
    if (!unsupportedCount) return null;
    return localization.DOWNLOADDIALOG.unsupportedFormatsWarning(unsupportedCount);
  },

  getLockedSettingTooltip() {
    return localization.DOWNLOADDIALOG.lockedSettingTooltip();
  },

  getSelectFormatFirstTooltip() {
    return localization.DOWNLOADDIALOG.selectFormatFirstTooltip;
  },

  getSuggestImprovementLocation() {
    return localization.DOWNLOADDIALOG.suggestImprovementLocation;
  },

  // Download flow manages its own dialog lifecycle (flying files animation).
  shouldDestroyOnSubmit: false,

  presetDialogStrings: {
    saveError: () => localization.DOWNLOADDIALOG.saveDownloadPresetError,
    removeError: () => localization.DOWNLOADDIALOG.removeDownloadPresetError,
    createPresetTitle: () => localization.DOWNLOADDIALOG.createDownloadPreset,
    savePresetTitle: () => localization.DOWNLOADDIALOG.saveDownloadPreset,
    removePresetTitle: () => localization.DOWNLOADDIALOG.removeDownloadPreset,
    removePresetText: (presetName) => localization.DOWNLOADDIALOG.removeDownloadPresetText(presetName),
  },

  buildSubmitPayload({
    selectedPresetId,
    selectedSettings,
    normalizedCrop,
    cropAspectPreset,
    selectionCount,
    copyPreviewAssetId,
    subscriptionFeatures,
    isAssetsWithWatermark,
    forceOriginalOnly,
    transformationMode,
  }) {
    // Website / proofing admin forced "download as original only":
    // strip format/crop/organize options and send a minimal payload.
    if (forceOriginalOnly) {
      const isWatermarkingAllowed = subscriptionFeatures?.watermarks;
      const displayWatermark = isWatermarkingAllowed || !isMainApp;
      const { isArchive, withoutWatermark } = selectedSettings || {};
      const config = {
        mimeType: 'original',
        organizeByCollections: false,
        isArchive: Boolean(isArchive),
        withoutWatermark: displayWatermark ? Boolean(withoutWatermark) : true,
      };
      if (isAssetsWithWatermark) config.ignoreCache = true;
      return config;
    }
    const config = buildDownloadSubmitConfig({
      selectedPreset: { _id: selectedPresetId, data: selectedSettings },
      isAssetsWithWatermark,
      subscriptionFeatures,
      normalizedCrop,
      cropAspectPreset,
      duplicateSelectionCount: selectionCount,
      copyPreviewAssetId,
    });
    if (transformationMode) {
      return { ...config, transformationMode };
    }
    return config;
  },
};
